import { isPlatformBrowser } from '@angular/common';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  CalendarDays,
  ChevronsLeftRightEllipsis,
  Clock3,
  LogOut,
  LucideAngularModule,
  Menu,
  MoonStar,
  SunMedium,
} from 'lucide-angular';
import { AuthService } from '../core/services/auth.service';
import { VendorContextService } from '../core/services/vendor-context.service';
import { LayoutPreferencesService } from './layout-preferences.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [LucideAngularModule],
  styles: [
    `
      :host {
        display: block;
        position: sticky;
        top: 0;
        z-index: 1035;
      }

      .portal-header {
        background: #ffffff;
        border-top: 3px solid #3d3d3d;
        border-bottom: 1px solid #d8deea;
        box-shadow: 0 2px 8px rgba(12, 34, 79, 0.06);
      }

      .portal-header__bar {
        min-height: 42px;
        padding: 0.35rem 0.8rem;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
      }

      .portal-header__brand-group,
      .portal-header__utilities {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        min-width: 0;
      }

      .portal-brand {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        white-space: nowrap;
      }

      .brand-mark {
        width: 14px;
        height: 18px;
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 2px;
      }

      .brand-mark span {
        display: block;
        border-radius: 2px;
        background: linear-gradient(180deg, #2447ff 0%, #1b4fb7 100%);
      }

      .brand-mark span:nth-child(1) {
        height: 8px;
        margin-top: 5px;
      }

      .brand-mark span:nth-child(2) {
        height: 8px;
      }

      .brand-mark span:nth-child(3) {
        height: 8px;
      }

      .brand-mark span:nth-child(4) {
        height: 8px;
        margin-top: -5px;
      }

      .brand-wordmark {
        font-size: 1.55rem;
        line-height: 1;
        letter-spacing: -0.05em;
      }

      .brand-dark {
        font-weight: 700;
        color: #2e3642;
      }

      .brand-accent {
        font-weight: 600;
        font-style: italic;
        color: #274eff;
      }

      .page-chip {
        display: inline-flex;
        align-items: center;
        padding: 0.28rem 0.7rem;
        border-radius: 999px;
        background: #edf3ff;
        color: #184b9a;
        font-size: 0.78rem;
        font-weight: 700;
        white-space: nowrap;
      }

      .utility-user-copy {
        display: flex;
        flex-direction: column;
        line-height: 1.05;
        color: #41526d;
      }

      .utility-user-line,
      .utility-name-line {
        display: inline-flex;
        align-items: center;
        gap: 0.3rem;
        font-size: 0.82rem;
        white-space: nowrap;
      }

      .utility-name-line {
        font-weight: 700;
        color: #1c4f9c;
      }

      .utility-chip {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        color: #4d5d75;
        font-size: 0.82rem;
        font-weight: 600;
        white-space: nowrap;
      }

      .utility-icon-shell,
      .utility-action,
      .user-trigger {
        width: 30px;
        height: 30px;
        border-radius: 999px;
        border: 1px solid #d5ddeb;
        background: #ffffff;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #64748b;
      }

      .utility-action {
        transition:
          border-color 0.18s ease,
          background-color 0.18s ease,
          color 0.18s ease;
      }

      .utility-action:hover,
      .user-trigger:hover {
        border-color: #afc1e4;
        background: #f3f7ff;
        color: #1c4f9c;
      }

      .user-trigger {
        position: relative;
        overflow: hidden;
        background: linear-gradient(180deg, #f8fbff 0%, #eef4ff 100%);
      }

      .avatar-circle {
        width: 100%;
        height: 100%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(180deg, #1593d8 0%, #0b69c5 100%);
        color: #ffffff;
        font-size: 0.78rem;
        font-weight: 800;
      }

      .user-menu-anchor {
        position: relative;
      }

      .user-menu-panel {
        position: absolute;
        top: calc(100% + 0.55rem);
        right: 0;
        min-width: 220px;
        background: #ffffff;
        border: 1px solid #d7deeb;
        border-radius: 0.75rem;
        box-shadow: 0 16px 40px rgba(11, 34, 77, 0.14);
        padding: 0.45rem;
      }

      .user-menu-meta {
        padding: 0.45rem 0.55rem 0.6rem;
        border-bottom: 1px solid #eef2f8;
        margin-bottom: 0.3rem;
      }

      .user-menu-name {
        font-size: 0.9rem;
        font-weight: 700;
        color: #1f2937;
      }

      .user-menu-subtitle {
        font-size: 0.78rem;
        color: #64748b;
      }

      .user-menu-btn {
        width: 100%;
        border: 0;
        background: transparent;
        border-radius: 0.55rem;
        padding: 0.6rem 0.7rem;
        display: flex;
        align-items: center;
        gap: 0.55rem;
        color: #314155;
        font-size: 0.87rem;
        font-weight: 600;
        text-align: left;
      }

      .user-menu-btn:hover {
        background: #f3f7ff;
        color: #164d9c;
      }

      @media (max-width: 991.98px) {
        .portal-header__bar {
          padding-inline: 0.65rem;
        }

        .utility-user-copy,
        .utility-chip {
          display: none;
        }
      }

      @media (max-width: 575.98px) {
        .portal-header__bar {
          gap: 0.5rem;
        }

        .brand-wordmark {
          font-size: 1.2rem;
        }

        .page-chip,
        .utility-icon-shell {
          display: none;
        }
      }
    `,
  ],
  template: `
    <header class="portal-header">
      <div class="portal-header__bar">
        <div class="portal-header__brand-group">
          <div class="portal-brand" aria-label="TradeYourAmp">
            <span class="brand-mark" aria-hidden="true">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </span>
            <div class="brand-wordmark">
              <span class="brand-dark">TradeYour</span><span class="brand-accent">Amp</span>
            </div>
          </div>

          @if (layout.navigationMode() === 'left') {
            <span class="page-chip d-none d-lg-inline-flex">{{ pageTitle() }}</span>
          }
        </div>

        <div class="portal-header__utilities">
          <div class="utility-user-copy d-none d-md-flex">
            <div class="utility-user-line">
              <lucide-icon [img]="SunIcon" [size]="14"></lucide-icon>
              <span>{{ greetingLabel() }},</span>
            </div>
            <div class="utility-name-line">{{ firstName() }}</div>
          </div>

          <div class="utility-chip d-none d-lg-inline-flex">
            <lucide-icon [img]="CalendarIcon" [size]="14"></lucide-icon>
            <span>{{ currentDateLabel() }}</span>
          </div>

          <div class="utility-chip d-none d-lg-inline-flex">
            <lucide-icon [img]="ClockIcon" [size]="14"></lucide-icon>
            <span>{{ currentTimeLabel() }}</span>
          </div>

          <span class="utility-icon-shell d-none d-sm-inline-flex" aria-hidden="true">
            <lucide-icon [img]="MoonIcon" [size]="15"></lucide-icon>
          </span>

          <button
            type="button"
            class="utility-action"
            [attr.aria-label]="menuToggleLabel()"
            [title]="menuToggleLabel()"
            (click)="toggleMenu()"
          >
            <lucide-icon [img]="MenuIcon" [size]="16"></lucide-icon>
          </button>

          <div class="user-menu-anchor">
            <button
              type="button"
              class="user-trigger"
              aria-label="Open user menu"
              aria-haspopup="menu"
              [attr.aria-expanded]="userMenuOpen()"
              (click)="toggleUserMenu()"
            >
              <span class="avatar-circle">{{ initials() }}</span>
            </button>

            @if (userMenuOpen()) {
              <div class="user-menu-panel" role="menu">
                <div class="user-menu-meta">
                  <div class="user-menu-name">{{ auth.user()?.full_name || 'User' }}</div>
                  @if (vendorContext.vendorName()) {
                    <div class="user-menu-subtitle">{{ vendorContext.vendorName() }}</div>
                  }
                  <div class="user-menu-subtitle text-capitalize">{{ (auth.user()?.role || 'viewer').replace('_', ' ') }}</div>
                </div>

                <button type="button" class="user-menu-btn" role="menuitem" (click)="toggleNavigationMode()">
                  <lucide-icon [img]="LayoutSwitchIcon" [size]="16"></lucide-icon>
                  <span>{{ navigationModeLabel() }}</span>
                </button>

                <button type="button" class="user-menu-btn" role="menuitem" (click)="signOut()">
                  <lucide-icon [img]="LogOutIcon" [size]="16"></lucide-icon>
                  <span>Sign Out</span>
                </button>
              </div>
            }
          </div>
        </div>
      </div>
    </header>
  `,
})
export class HeaderComponent implements OnInit, OnDestroy {
  protected readonly auth = inject(AuthService);
  protected readonly vendorContext = inject(VendorContextService);
  protected readonly layout = inject(LayoutPreferencesService);
  private readonly router = inject(Router);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly browser = isPlatformBrowser(this.platformId);
  private clockId?: number;

  protected readonly userMenuOpen = signal(false);
  private readonly now = signal(new Date());

  protected readonly SunIcon = SunMedium;
  protected readonly CalendarIcon = CalendarDays;
  protected readonly ClockIcon = Clock3;
  protected readonly MoonIcon = MoonStar;
  protected readonly MenuIcon = Menu;
  protected readonly LayoutSwitchIcon = ChevronsLeftRightEllipsis;
  protected readonly LogOutIcon = LogOut;
  protected readonly initials = computed(() =>
    (this.auth.user()?.full_name || 'U')
      .split(' ')
      .filter(Boolean)
      .map((item) => item[0]?.toUpperCase())
      .join('')
      .slice(0, 2),
  );
  protected readonly firstName = computed(() => (this.auth.user()?.full_name || 'User').split(' ')[0] || 'User');
  protected readonly greetingLabel = computed(() => {
    const hour = this.now().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  });
  protected readonly currentDateLabel = computed(() =>
    new Intl.DateTimeFormat(undefined, {
      weekday: 'long',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(this.now()),
  );
  protected readonly currentTimeLabel = computed(() =>
    new Intl.DateTimeFormat(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(this.now()),
  );
  protected readonly menuToggleLabel = computed(() => {
    if (this.layout.navigationMode() === 'top') {
      return this.layout.topDrawerOpen() ? 'Hide left menu' : 'Show left menu';
    }
    return this.layout.sidebarCollapsed() ? 'Expand menu' : 'Collapse menu';
  });
  protected readonly navigationModeLabel = computed(() =>
    this.layout.navigationMode() === 'top' ? 'Switch to side menu' : 'Switch to top menu',
  );
  protected readonly pageTitle = computed(() => {
    const url = this.router.url;
    if (url === '/') return 'Dashboard';
    if (url.startsWith('/vendors/')) return 'Vendor Details';
    if (url.startsWith('/vendors')) return 'Vendors';
    if (url.startsWith('/document-configurations') || url.startsWith('/vendor-documents')) return 'Document Configuration';
    if (url.includes('/loas/new')) return 'New LOA';
    if (url.includes('/loas/') && url.includes('/edit')) return 'Edit LOA';
    if (url.startsWith('/loas')) return 'LOA Management';
    if (url.includes('/contracts/new')) return 'New Contract';
    if (url.includes('/contracts/') && url.includes('/edit')) return 'Edit Contract';
    if (url.startsWith('/contracts/analysis')) return 'Contracts Analysis';
    if (url.startsWith('/contracts')) return 'Contract Management';
    if (url.includes('/applications/new')) return 'New Application';
    if (url.includes('/applications/') && url.includes('/edit')) return 'Edit Application';
    if (url.startsWith('/application-approvals')) return 'Application Approval';
    if (url.startsWith('/payment-knock-off')) return 'Payment Knock-Off';
    if (url.startsWith('/applications')) return 'Applications';
    if (url.startsWith('/sop-tmr-billing')) return 'SOP/TMR Billing';
    if (url.startsWith('/oa-billing')) return 'OA Billing Management';
    if (url.startsWith('/bill-verification1')) return 'Bill Verification1';
    if (url.startsWith('/bill-verification')) return 'Bill Verification';
    if (url.includes('/credit-debit-notes/') && url.includes('/edit')) return 'Edit Credit/Debit Note';
    if (url.startsWith('/credit-debit-notes/new')) return 'Add Credit/Debit Note';
    if (url.startsWith('/credit-debit-notes')) return 'Credit/Debit Notes';
    if (url.startsWith('/staff')) return 'Staff Management';
    if (url.startsWith('/auto-fetch/all-rea')) return 'All REA';
    if (url.startsWith('/auto-fetch/forecast-market-prices')) return 'Forecast Market Price';
    if (url.startsWith('/auto-fetch/grid-india-schedules')) return 'Grid India Schedules';
    if (url.startsWith('/bess/fleet-operations-matrix')) return 'Fleet Operations Matrix';
    if (url.startsWith('/bess/ai-forecast')) return 'AI Forecast';
    if (url.startsWith('/bess/place-bid')) return 'Place Bid';
    if (url.startsWith('/bess/contract-hedging-optimization')) return 'Contract Hedging & Optimization';
    return 'Dashboard';
  });

  ngOnInit(): void {
    if (!this.browser) return;
    this.clockId = window.setInterval(() => this.now.set(new Date()), 1000);
  }

  ngOnDestroy(): void {
    if (typeof this.clockId === 'number') {
      window.clearInterval(this.clockId);
    }
  }

  protected toggleMenu(): void {
    if (this.layout.navigationMode() === 'top') {
      this.layout.toggleTopDrawer();
      return;
    }

    this.layout.toggleSidebarCollapsed();
  }

  protected toggleNavigationMode(): void {
    this.userMenuOpen.set(false);
    this.layout.toggleNavigationMode();
  }

  protected toggleUserMenu(): void {
    this.userMenuOpen.update((value) => !value);
  }

  protected signOut(): void {
    this.userMenuOpen.set(false);
    this.auth.logout();
  }

  @HostListener('document:click', ['$event'])
  protected handleDocumentClick(event: MouseEvent): void {
    const target = event.target;
    if (!(target instanceof Node)) return;
    if (this.elementRef.nativeElement.contains(target)) return;
    this.userMenuOpen.set(false);
  }
}
